import { create } from 'zustand';

export type Theme = 'system' | 'light' | 'dark';

const KEY = 'codeframe.theme';

function readInitial(): Theme {
  if (typeof localStorage === 'undefined') return 'system';
  const v = localStorage.getItem(KEY);
  return v === 'light' || v === 'dark' || v === 'system' ? v : 'system';
}

interface ThemeStore {
  theme: Theme;
  setTheme: (t: Theme) => void;
}

export const useTheme = create<ThemeStore>((set) => ({
  theme: readInitial(),
  setTheme: (t) => {
    if (typeof localStorage !== 'undefined') {
      try {
        if (t === 'system') localStorage.removeItem(KEY);
        else localStorage.setItem(KEY, t);
      } catch {
        /* quota / disabled */
      }
    }
    set({ theme: t });
    applyColorScheme(t);
  },
}));

/**
 * Reflect the theme onto <html>. We set THREE things so both color mechanisms
 * resolve correctly:
 * - `color-scheme` style — drives `light-dark()` color tokens in index.css
 * - the `.light`/`.dark` class — drives the rare Tailwind `dark:` utilities
 *   (via `@custom-variant dark`) and lets `color-scheme` pin override system
 * - the `<meta name="color-scheme">` content — keeps UA form controls / scrollbars in sync
 *
 * Modes:
 * - 'system' → clear the pin + classes + reset meta to "light dark"; OS pref takes over
 * - 'light' / 'dark' → pin all three to the explicit value
 */
export function applyColorScheme(theme: Theme): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const meta = document.querySelector('meta[name="color-scheme"]');
  root.classList.remove('light', 'dark');
  if (theme === 'system') {
    root.style.colorScheme = '';
    meta?.setAttribute('content', 'light dark');
  } else {
    root.classList.add(theme);
    root.style.colorScheme = theme;
    meta?.setAttribute('content', theme);
  }
}

/** Compute the effective theme for UI hints (e.g., toggle icon). */
export function effectiveTheme(theme: Theme): 'light' | 'dark' {
  if (theme !== 'system') return theme;
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** Wire the system-pref listener so 'system' mode tracks live OS changes. */
export function installSystemListener(): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) return () => {};
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  const onChange = () => {
    // Only re-render store consumers — no need to mutate DOM class because
    // CSS media query under the no-class selector already toggled.
    useTheme.setState((s) => ({ theme: s.theme }));
  };
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
}
